import React, { useState, useEffect } from 'react';
import { siteConfigApi, adApi } from '../utils/api';
import { useLanguageStore } from '../stores/languageStore';
import { t } from '../stores/languageStore';

const BusinessCooperation: React.FC = () => {
  const { language } = useLanguageStore();
  const [configs, setConfigs] = useState<any>({
    business_cooperation_title: '商务合作',
    business_cooperation_description: '我们欢迎各类与社区发展相关的商务合作',
    business_cooperation_email: 'contact@taohuatan.com',
    business_cooperation_phone: '400-123-4567',
    business_cooperation_whatsapp: 'TaoHuaTan',
    business_cooperation_telegram: 'TaoHuaTan',
    business_cooperation_content: '我们提供以下商务合作方式：\n\n1. **广告投放**：在论坛各页面投放广告，提高品牌曝光度\n2. **内容合作**：合作发布优质内容和文章\n3. **产品推广**：推广优质的产品和服务\n4. **技术合作**：共同开发相关工具和服务\n5. **活动合作**：合作举办线上线下交流活动\n\n请通过以上联系方式与我们取得联系，我们会在24小时内回复您。'
  });
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState<any[]>([]);

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const response = await siteConfigApi.getPublicConfigs();
        if (response.success && response.data) {
          setConfigs(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.error('获取配置失败:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchPartners = async () => {
      try {
        const response = await adApi.getActiveAds();
        if (response.success && response.data) {
          setPartners(response.data.business_partners || []);
        }
      } catch (error) {
        console.error('获取合作伙伴失败:', error);
      }
    };

    fetchConfigs();
    fetchPartners();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('businessCooperation.loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-12 px-4 sm:px-6 lg:px-8" style={{ height: '811px' }}>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{configs.business_cooperation_title}</h1>
            <p className="text-lg text-gray-600">
              {configs.business_cooperation_description}
            </p>
          </div>
          
          <div className="bg-white shadow rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">{t('businessCooperation.cooperationTypes')}</h2>
            <div className="whitespace-pre-line text-gray-600">
              {configs.business_cooperation_content}
            </div>
          </div>
          
          <div className="bg-white shadow rounded-lg p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">{t('businessCooperation.contactInfo')}</h2>
            <div className="space-y-4">
              {configs.business_cooperation_email && (
                <div className="flex items-center space-x-4">
                  <span className="text-gray-500 w-24">{language === 'en' ? 'Email:' : '邮箱：'}</span>
                  <span className="text-gray-900">{configs.business_cooperation_email}</span>
                </div>
              )}
              {configs.business_cooperation_phone && (
                <div className="flex items-center space-x-4">
                  <span className="text-gray-500 w-24">{language === 'en' ? 'Phone:' : '电话：'}</span>
                  <span className="text-gray-900">{configs.business_cooperation_phone}</span>
                </div>
              )}
              {configs.business_cooperation_whatsapp && (
                <div className="flex items-center space-x-4">
                  <span className="text-gray-500 w-24">{language === 'en' ? 'WhatsApp:' : 'WhatsApp：'}</span>
                  <span className="text-gray-900">{configs.business_cooperation_whatsapp}</span>
                </div>
              )}
              {configs.business_cooperation_telegram && (
                <div className="flex items-center space-x-4">
                  <span className="text-gray-500 w-24">{language === 'en' ? 'Telegram:' : 'Telegram：'}</span>
                  <span className="text-gray-900">{configs.business_cooperation_telegram}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {partners.length > 0 && (
        <div className="bg-gray-50 py-12 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold text-gray-900 text-center mb-8">{t('businessCooperation.strategicPartners')}</h2>
            <div className="flex flex-wrap justify-start gap-8" style={{ width: '1100px' }}>
              {partners.map((partner) => {
                // 构建完整的URL
                const fullUrl = partner.link_url.startsWith('http://') || partner.link_url.startsWith('https://') 
                  ? partner.link_url 
                  : `https://${partner.link_url}`;
                
                // 提取域名以获取favicon
                const getFaviconUrl = (url: string) => {
                  try {
                    const parsedUrl = new URL(url);
                    return `${parsedUrl.protocol}//${parsedUrl.hostname}/favicon.ico`;
                  } catch {
                    return '';
                  }
                };
                
                // 处理广告点击，先统计点击次数，再跳转
                const handleAdClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  try {
                    // 调用点击统计接口
                    await adApi.clickAd(partner.id);
                    // 跳转链接
                    window.open(fullUrl, '_blank');
                  } catch (error) {
                    console.error('统计点击失败:', error);
                    // 即使统计失败也跳转到链接
                    window.open(fullUrl, '_blank');
                  }
                };
                
                return (
                  <a
                    key={partner.id}
                    href={fullUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center justify-center p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200"
                    style={{ width: '60px' }}
                    onClick={handleAdClick}
                  >
                    <div className="w-16 h-16 flex items-center justify-center mb-4">
                      {partner.image_url ? (
                        <img
                          src={partner.image_url}
                          alt={partner.title}
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <img
                          src={getFaviconUrl(fullUrl)}
                          alt={`${partner.title} favicon`}
                          className="w-12 h-12 object-contain"
                          onError={(e) => {
                            // 当favicon加载失败时，使用默认图标
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMjAgMzZjOC44MzcgMCAxNi03LjE2MyAxNi0xNnMtNy4xNjMtMTYtMTYtMTYtMTYgNy4xNjMtMTYgMTYgNy4xNjMgMTYgMTYgMTZ6bTAtMmM3Ljc1MyAwIDE0LTYuMjQ3IDE0LTE0cy02LjI0Ny0xNC0xNC0xNC0xNCA2LjI0Ny0xNCAxNCA2LjI0NyAxNCAxNCAxNHptMCAxYy0xLjExIDAtMi0wLjg5LTItMnMwLjg5LTIgMi0yIDIgMC44OSAyIDIgMC44OSAyIDItMnoiIGZpbGw9IiM1NTUiLz48L3N2Zz4=';
                          }}
                        />
                      )}
                    </div>
                    <h3 className="text-gray-900 text-xs font-medium text-center writing-vertical-rl text-orientation-upright h-16 flex items-center justify-center">
                      {partner.title}
                    </h3>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessCooperation;